import React, {useEffect, useState} from "react";
import {ArrowDownLeftIcon, ArrowUpRightIcon} from "@heroicons/react/24/solid";
import {detectBaseUrl, SpeechRecognitionEngine, SpeechSearchModal} from "../VoiceSearch";
import AudioVisualizer from "../AudioVisualizer";
import VoiceReminderTooltip from "./components/VoiceReminderTooltip";
import {startRecoSession, endRecoSessionAndSend, isRecoSessionActive} from "../common/services";
import {getIsUseWakeWordEnv, useMicPermission, computeShouldEnableWakeWord} from "./utils/useMicPermission";

/**
 * VoiceManagerContainer component
 * Main component that manages voice search functionality and user interaction
 */
const VoiceManagerContainer = ({onModalOpen, onModalClose, onWidgetMinimize, onWidgetRestore}) => {
    const [showModal, setShowModal] = useState(false);
    const [showReminder, setShowReminder] = useState(true);
    const [currentText, setCurrentText] = useState("");
    const [currentHold, setCurrentHold] = useState(null);
    const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);

    const isUseWakeWordEnv = getIsUseWakeWordEnv();

    const micGranted = useMicPermission(showModal);

    const shouldEnableWakeWord = computeShouldEnableWakeWord(isUseWakeWordEnv, micGranted);

    let baseUrl = detectBaseUrl();


    const setModalOpenFlag = (val) => {
        try {
            sessionStorage.setItem('reco_modal_open', val ? '1' : '0');
        } catch (_) {
        }
    };

    const minimizeWidget = () => {
        setIsWidgetMinimized(true);
        if (onWidgetMinimize) onWidgetMinimize();
    };

    const restoreWidget = () => {
        setIsWidgetMinimized(false);
        if (onWidgetRestore) onWidgetRestore();
    };

    const openModal = () => {
        setShowModal(true);
        setModalOpenFlag(true);
        try {
            startRecoSession();
        } catch (_) {
        }
        if (onModalOpen) onModalOpen();
    };

    const closeModal = () => {
        setShowModal(false);
        setModalOpenFlag(false);
        setCurrentText("");
        try {
            endRecoSessionAndSend();
        } catch (_) {
        }
        if (onModalClose) onModalClose();
        if (localStorage.getItem("recodemo") === "1") {
            const recomdoURL = process.env.REACT_APP_DEMO_URL;
            const windowName = "recomdoWindow";

            const newWindow = window.open(recomdoURL, windowName);

            if (newWindow) {
                newWindow.focus();
            } else {
                window.location.href = recomdoURL;
            }
        }
    };

    const recoSimilarWords = ["reco", "recko", "rekko", "reko", "rekoh", "reeco", "reeko", "rheco", "rieco",
        "rico", "rikko", "riko"];

    const handleTranscriptUpdate = (data) => {
        let newText = '';
        let hold = null;

        if (typeof data === 'string') {
            newText = data;
        } else if (data && typeof data === 'object') {
            newText = data.text || '';
            hold = data.hold;
            if (typeof hold === 'boolean') {
                setCurrentHold(hold);
            }
        }

        const lowerText = newText.toLowerCase();
        const containsRecoOrSimilar = recoSimilarWords.some(word => lowerText.includes(word));

        if (!newText || newText.length === 0) return;

        if (shouldEnableWakeWord && containsRecoOrSimilar && !showModal) {
            openModal();
            return;
        }

        setCurrentText(newText);
    };

    useEffect(() => {
        const hiddenEndTimeoutRef = {current: null};
        const HIDDEN_END_DELAY_MS = 60000;

        const scheduleEndOnHidden = () => {
            try {
                if (!showModal) return;
                if (hiddenEndTimeoutRef.current) return;
                hiddenEndTimeoutRef.current = setTimeout(() => {
                    try {
                        const modalStillOpen = (sessionStorage.getItem('reco_modal_open') === '1') || showModal;
                        if (modalStillOpen && typeof isRecoSessionActive === 'function' && isRecoSessionActive()) {
                            endRecoSessionAndSend();
                        }
                    } catch (_) {
                    } finally {
                        hiddenEndTimeoutRef.current = null;
                    }
                }, HIDDEN_END_DELAY_MS);
            } catch (_) {
            }
        };

        const cancelScheduledHiddenEnd = () => {
            try {
                if (hiddenEndTimeoutRef.current) {
                    clearTimeout(hiddenEndTimeoutRef.current);
                    hiddenEndTimeoutRef.current = null;
                }
            } catch (_) {
            }
        };

        const onVisibilityChange = () => {
            try {
                if (document.visibilityState === 'hidden') {
                    scheduleEndOnHidden();
                } else if (document.visibilityState === 'visible') {
                    cancelScheduledHiddenEnd();
                    const modalOpen = (sessionStorage.getItem('reco_modal_open') === '1') || showModal;
                    if (modalOpen && typeof isRecoSessionActive === 'function' && !isRecoSessionActive()) {
                        startRecoSession();
                    }
                }
            } catch (_) {
            }
        };

        const endImmediatelyWithBeacon = () => {
            try {
                if (showModal) {
                    setModalOpenFlag(true);
                    endRecoSessionAndSend(true);
                }
            } catch (_) {
            }
        };

        const onPageHide = () => endImmediatelyWithBeacon();
        const onBeforeUnload = () => endImmediatelyWithBeacon();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('beforeunload', onBeforeUnload);

        return () => {
            cancelScheduledHiddenEnd();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [showModal]);

    return (
        <>
            {shouldEnableWakeWord && (
                <SpeechRecognitionEngine onTranscriptUpdate={handleTranscriptUpdate} isItModal={false} initLoad={true}
                                         isModalOpen={showModal}/>
            )}
            {!showModal && !isWidgetMinimized && (
                <div className="fixed bottom-0 right-5 z-[999] flex flex-col items-end">
                    {showReminder && (
                        <VoiceReminderTooltip onClose={() => setShowReminder(false)}
                                              message={shouldEnableWakeWord ? undefined : "Click for voice search."}/>
                    )}
                    <div className="relative">
                        <button
                            className="border-none bg-[#F9F9F9] text-black p-2 min-[450px]:p-3 min-[550px]:p-5 shadow-2xl shadow-black rounded-tl-lg rounded-tr-lg flex items-center"
                            onClick={openModal}
                        >
                            <div className="overflow-hidden w-80">
                                {shouldEnableWakeWord ? (
                                    <AudioVisualizer width="320" height="50" color="#F9F9F9" is_opning={false}
                                                     barCount={300}/>
                                ) : (
                                    <img src={`${baseUrl}/animations/visualization.gif`} alt="Visualization" width="300"
                                         height="50" style={{backgroundColor: "#F9F9F9"}}/>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={minimizeWidget}
                            className="absolute -top-2 -right-2 bg-white w-6 h-6 text-xs leading-none rounded-full shadow border border-black/10 flex items-center justify-center"
                            aria-label="Minimize voice trigger"
                        >
                            <ArrowDownLeftIcon className="w-3 h-3 text-black" />
                        </button>
                    </div>
                </div>
            )}
            {!showModal && isWidgetMinimized && (
                <div className="fixed bottom-0 right-5 z-[999]">
                    <div className="relative">
                        <button
                            onClick={restoreWidget}
                            className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-tl-lg rounded-tr-lg shadow border border-black/10"
                            aria-label="Restore voice trigger"
                        >
                            <AudioVisualizer
                                width={window.innerWidth >= 640 ? "140" : "120"}
                                height={window.innerWidth >= 640 ? "40" : "35"}
                                color="#F9F9F9"
                                is_opning={true}
                                barCount={window.innerWidth >= 640 ? "140" : "120"}
                            />
                        </button>
                        <button
                            onClick={restoreWidget}
                            className="absolute -top-2 -right-2 bg-white w-6 h-6 text-xs leading-none rounded-full shadow border border-black/10 flex items-center justify-center"
                            aria-label="Maximize voice trigger"
                            title="Maximize"
                        >
                            <ArrowUpRightIcon className="w-3 h-3 text-black" />
                        </button>
                    </div>
                </div>
            )}
            {showModal &&

                <>
                    {!shouldEnableWakeWord && (
                        <SpeechRecognitionEngine
                            onTranscriptUpdate={handleTranscriptUpdate}
                            isItModal={false}
                            initLoad={true}
                            isModalOpen={showModal}
                        />
                    )}

                    <SpeechSearchModal onClose={closeModal} handleTranscriptUpdate={handleTranscriptUpdate}
                                       currentText={currentText}
                                       currentHold={currentHold}/>
                </>

            }
        </>
    );
};

export default VoiceManagerContainer;
